import { useEffect, useMemo, useState } from "react";
import { Plus, Users, Search, ArrowDownWideNarrow, ArrowUpWideNarrow, Archive, Sparkles } from "lucide-react";
import { api, formatTL } from "@/lib/api";
import { CUSTOMER_TYPES, TYPE_COLOR, SOURCES, scoreColor, timeAgo } from "@/lib/crmOptions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ViewToggle, useViewMode } from "@/components/ViewToggle";
import { CustomerForm } from "@/components/crm/CustomerForm";
import { CustomerDetail } from "@/components/crm/CustomerDetail";
import { Pager, usePaged } from "@/components/Pager";


const norm = (s) => (s || "").toLocaleLowerCase("tr-TR");
const place = (c) => [c.desired_district, c.desired_city].filter(Boolean).join(", ") || "-";
const budget = (c) => (c.budget_min || c.budget_max ? `${formatTL(c.budget_min)} – ${formatTL(c.budget_max)}` : "-");

const MatchBadge = ({ c }) => c.best_match ? (
  <span data-testid={`customer-match-${c.id}`} className={`inline-flex items-center gap-1 text-white text-xs font-bold px-2 py-0.5 rounded-full ${scoreColor(c.best_match)}`}>
    <Sparkles className="w-3 h-3" />%{c.best_match} · {c.match_count}
  </span>
) : <span className="text-xs text-slate-400">—</span>;

export default function CustomersCrmPage() {
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("ALL");
  const [source, setSource] = useState("ALL");
  const [q, setQ] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [mode, setMode] = useViewMode("customers");
  const [form, setForm] = useState(() => (new URLSearchParams(window.location.search).get("new") ? { editing: null } : null));
  const [detailId, setDetailId] = useState(null);

  const load = async () => setItems((await api.get("/customers", { params: { archived: tab === "archive" } })).data);
  useEffect(() => { load(); }, [tab]);

  const counts = useMemo(() => Object.fromEntries(Object.keys(CUSTOMER_TYPES).map((k) => [k, items.filter((c) => c.customer_type === k).length])), [items]);
  const shown = items
    .filter((c) => (category === "ALL" || c.customer_type === category) && (source === "ALL" || c.source === source))
    .filter((c) => !q || [c.full_name, c.phone, c.preferences, c.desired_city, c.desired_district].some((v) => norm(v).includes(norm(q))))

    .sort((a, b) => (newestFirst ? -1 : 1) * (a.last_contact_at || "").localeCompare(b.last_contact_at || ""));
  const paged = usePaged(shown);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">CRM</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Müşteriler</h1>
          <p className="text-slate-500 mt-1">Ofise gelen ve arayan müşteriler. Talepler yayındaki portföylerle otomatik eşleştirilir.</p>
        </div>
        <Button data-testid="add-customer-btn" onClick={() => setForm({ editing: null })} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Yeni Müşteri</Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger data-testid="tab-active-customers" value="active"><Users className="w-3.5 h-3.5 mr-1.5" />Aktif</TabsTrigger>
            <TabsTrigger data-testid="tab-archive-customers" value="archive"><Archive className="w-3.5 h-3.5 mr-1.5" />Arşiv</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-3">
          <span data-testid="customer-result-count" className="text-sm text-slate-500">{shown.length} müşteri</span>
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["ALL", "Tümü", items.length], ...Object.entries(CUSTOMER_TYPES).map(([k, l]) => [k, l, counts[k]])].map(([k, l, n]) => (
          <button key={k} data-testid={`category-${k}`} onClick={() => setCategory(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {l} ({n})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="customer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, telefon, talep veya bölge ara..." className="pl-10" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger data-testid="customer-source-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Tüm geliş şekilleri</SelectItem>{Object.entries(SOURCES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" data-testid="customer-sort-toggle" onClick={() => setNewestFirst(!newestFirst)}>
          {newestFirst ? <><ArrowDownWideNarrow className="w-4 h-4 mr-1.5" />Yeniden eskiye</> : <><ArrowUpWideNarrow className="w-4 h-4 mr-1.5" />Eskiden yeniye</>}
        </Button>
      </div>

      {shown.length === 0 ? (
        <Card data-testid="customers-empty" className="p-16 text-center text-slate-500 border-dashed"><Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />{items.length ? "Filtreye uyan müşteri yok." : tab === "archive" ? "Arşivde müşteri yok." : "Henüz müşteri eklenmemiş."}</Card>
      ) : mode === "list" ? (
        <Card className="overflow-x-auto" data-testid="customers-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Müşteri", "Kategori", "Geliş", "Telefon", "Bütçe", "Bölge", "Uyum", "Son Temas"].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {paged.items.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => setDetailId(c.id)} className="border-t cursor-pointer hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5"><div className="font-semibold">{c.full_name}</div><div className="text-xs text-slate-500 truncate max-w-[220px]">{c.preferences}</div></td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className={TYPE_COLOR[c.customer_type]}>{CUSTOMER_TYPES[c.customer_type]}</Badge></td>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">{SOURCES[c.source] || "-"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{c.phone}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs">{budget(c)}</td>
                  <td className="px-4 py-2.5 text-xs">{place(c)}</td>
                  <td className="px-4 py-2.5"><MatchBadge c={c} /></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{timeAgo(c.last_contact_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="customers-grid-view">
          {paged.items.map((c) => (
            <Card key={c.id} data-testid={`customer-card-${c.id}`} onClick={() => setDetailId(c.id)} className="p-5 cursor-pointer hover:shadow-md transition-shadow space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{c.full_name}</div>
                <MatchBadge c={c} />
              </div>
              <div className="flex gap-1.5 flex-wrap"><Badge variant="outline" className={TYPE_COLOR[c.customer_type]}>{CUSTOMER_TYPES[c.customer_type]}</Badge><Badge variant="outline">{SOURCES[c.source] || "-"}</Badge></div>
              <div className="text-sm text-slate-600">{c.phone}</div>
              <div className="text-xs text-slate-500">{budget(c)} · {place(c)}</div>
              <div className="text-xs text-slate-400 pt-2 border-t">Son temas: {timeAgo(c.last_contact_at)}</div>
            </Card>
          ))}
        </div>
      )}
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} testid="customers-pager" />

      <CustomerForm open={!!form} editing={form?.editing} onClose={() => setForm(null)} onSaved={() => { load(); }} />
      <CustomerDetail customerId={detailId} onClose={() => setDetailId(null)} onChanged={load} onEdit={(c) => { setDetailId(null); setForm({ editing: c }); }} />
    </div>
  );
}
